import React, { useState, useEffect} from 'react';
import { Dropdown, Modal, Tab, Tabs } from 'react-bootstrap';

import TagsTable from '../../components/Entities/TagsTable';
import ExtraDataForm from './ExtraDataForm';
import DataOpsExtraData from './DataOpsExtraData';
import { stringIsNumber, getTeamData } from '../../helpers/helpers';
import { ENTITY_GROUPS, ENTITY_MODELS, ENTITY_STATUS_TYPES, ENTITY_TYPES, ENTITY_VALUES, FILTER_KEYS, LEAGUE, LEAGUES, SCOPE, POSITION } from '../../config/constants';
import { entitiesPageValidator } from '../../helpers/validators';
import { useRouter } from 'helpers/customHooks';
import { EntityModalProps, ExtraData, Tag } from './types';
import { useEntitiesState, useFiltersState, useUserState } from 'store/hooks';
import EntityTab from 'components/Health/EntityTab';

const EntityModal = ({ show, onHide, addMode, selectedEntity, teamId, createEntity, editEntity, onExited, isToday} : EntityModalProps) => {
  const initialExtraData = {
    status: '',
    firstName: '',
    weight: '',
    lastName: '',
    teamId: '',
    birthDate: '',
    height: '',
    nflId: '',
    position: '',
    jerseyNumber: null,
    fitnessLevel: '',
    collegeName: '',
    positionGroup: '',
    middleName: '',
    footballName: '',
    displayName: '',
    week: '',
    homeTown: '',
    collegeId: '',
    gsisId: '',
    season: '',
    seasonType: '',
    esbId: '',
    currentTeamId: '',
    teamSeq: '',
    stats: '',
  };
  const initialEntity = {
    group: String(teamId),
    name: null,
    namespace: '',
    namespaced_id: '',
    model: ENTITY_VALUES.PLAYER,
    extra_data: initialExtraData,
    type: ENTITY_VALUES.PLAYER,
    id: null,
    tags: []
  };

  const teamData = getTeamData(teamId);

  const {isDataOpsPage, isNcaaLeague } = useEntitiesState();
  // number values are initially assigned null
  const getInitialEntity = () => {
    return initialEntity;
  };
  const [entity, setEntity] = useState(selectedEntity ? JSON.parse(JSON.stringify(selectedEntity)) : getInitialEntity());
  const [extraData, setExtraData] = useState<ExtraData>(initialExtraData);

  // this state variable handles which tab is currently active
  const [activeTab, setActiveTab] = useState('tags');

  useEffect(() => {//if no psgr set psgr
    if (selectedEntity) {
      setEntity(JSON.parse(JSON.stringify(selectedEntity)));
    } else {
      setEntity(getInitialEntity());
    }
    if((extraData.positionGroup === undefined && !addMode) && teamData?.league != 'ncaa-mlax'){
      setExtraData({ ...extraData, positionGroup: '' });
    }
    return () => setEntity(getInitialEntity()); //clean modal
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, extraData.positionGroup]);  

  useEffect(() => {
    if (teamId) {
      setEntity({ ...entity, group: ENTITY_GROUPS(teamId)[0] });
    }
  }, []);

  // if ball or object is suddenly selected, tab must switch to tags tab immediately
  useEffect(() => {
    if ((entity.type === ENTITY_VALUES.BALL || entity.type === ENTITY_VALUES.OBJECT) && addMode) {
      setActiveTab('tags');
      setExtraData({ ...extraData, status: ENTITY_STATUS_TYPES.ACT });
    }
    if (entity.type && addMode) {
      setEntity({ ...entity, model: ENTITY_MODELS(entity.type)[0].value });
    }
  }, [entity.type]);
  // const changeEntityType = type =>

  const changeEntityModel = (model:string) => setEntity({ ...entity, model: model });
  const changeEntityGroup = (group:string) => setEntity({ ...entity, group: group });
  const teamLeague = teamData?.league;
  const handleSaveClick = () => {
    const { status } = extraData;
    
    const entityExtraData = entity && entity.type === ENTITY_VALUES.BALL || entity.type === ENTITY_VALUES.OBJECT ? { status } : extraData;
    if (addMode) {
      createEntity(entity, entity.type !== ENTITY_VALUES.BALL && entity.type !== ENTITY_VALUES.OBJECT ? 
        {...entityExtraData,  jerseyNumber:entityExtraData.jerseyNumber? entityExtraData.jerseyNumber: 0}: entityExtraData);
    } else {
      if(teamData?.league !=="ncaa-mlax"){
        editEntity(entity, entity.type !== ENTITY_VALUES.BALL && entity.type !== ENTITY_VALUES.OBJECT ? 
          {...entityExtraData, nflId:entityExtraData.nflId? entityExtraData.nflId : '',
          jerseyNumber:entityExtraData.jerseyNumber? entityExtraData.jerseyNumber: 0, positionGroup: entityExtraData? entityExtraData.positionGroup === ''? 
          POSITION.types.find(position => position.name === entityExtraData.position)?.value.group :
          entityExtraData.positionGroup: '', stats: entityExtraData.stats? entityExtraData.stats : '' } : {...entityExtraData, positionGroup: entityExtraData? entityExtraData.positionGroup === ''? 
          POSITION.types.find(position => position.name === entityExtraData.position)?.value.group :
          entityExtraData.positionGroup: ''}, teamLeague);
      }else{
        editEntity(entity, entityExtraData, teamLeague);
      }
      
    }
    onHide();
  };

  const changeEntityId = (event:React.ChangeEvent<HTMLInputElement>) => {
    if ((event.target.value !== ' ' && stringIsNumber(event.target.value)) ||
      event.target.value === '') {
      setEntity({ ...entity, id: parseInt(event.target.value) });
    }
  };

  const changeEntityName = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      setEntity({ ...entity, name: event.target.value });
    }
  };

  const changeEntityNamespaceId = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      setEntity({ ...entity, namespaced_id: event.target.value });
    }
  };

  const changeNflId = (event:React.ChangeEvent<HTMLInputElement>) => {
    if ((stringIsNumber(event.target.value)) ||
      event.target.value === '') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.nflId = parseInt(event.target.value);
      setExtraData(newExtraData);
      setEntity({ ...entity, extra_data: newExtraData });
    }
  };

  const changeFirstName = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.firstName = event.target.value;
      setExtraData(newExtraData);
      //setEntity({...entity, extra_data: {...entity.extra_data, firstName: event.target.value}});
    }
  };

  const changeLastName = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.lastName = event.target.value;
      setExtraData(newExtraData);
      //setEntity({...entity,extra_data: {...entity.extra_data, lastName: event.target.value}});
    }
  };

  const changeJerseyNumber = (event:React.ChangeEvent<HTMLInputElement>) => {
    const newExtraData = JSON.parse(JSON.stringify(extraData));
    if ((event.target.value === ' '|| event.target.value === '')){
      newExtraData.jerseyNumber = '';
    }else{
      newExtraData.jerseyNumber = event.target.value.replace(/([.,<>'";*+?^=!¿¡¨´°¬:${}()@#%&~`|\-\\_\\[\]\/\\])/gi, "") ;
    }
      setExtraData(newExtraData);
      //setEntity({...entity,extra_data: {...entity.extra_data, jerseyNumber: parseInt(event.target.value)}});
  };

  const changeHeight = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.height = event.target.value;
      setExtraData(newExtraData);
      //setEntity({...entity,extra_data: {...entity.extra_data, height: event.target.value}});
    }
  };

  const changeWeight = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.weight = event.target.value;
      setExtraData(newExtraData);
      //setEntity({...entity,extra_data: {...entity.extra_data, weight: event.target.value}});
    }
  };

  const changeBirthday = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.birthDate = event.target.value;
      setExtraData(newExtraData);
      //setEntity({...entity,extra_data: {...entity.extra_data, birthDate: event.target.value}});
    }
  };

  const changeCollegeName = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.collegeName = event.target.value;
      setExtraData(newExtraData);
    }
  };

  const changeTagNamePlayer = (value:string, index:number) => {
    if (value !== ' ') {
      const newEntity = JSON.parse(JSON.stringify(entity));
      newEntity.tags[index].name = value;
      setEntity(newEntity);
    }
  };

  const changeTagGroupPlayer = (value:string, index:number) => {
    if (value !== ' ') {
      const newEntity = JSON.parse(JSON.stringify(entity));
      newEntity.tags[index].group = value;
      setEntity(newEntity);
    }
  };

  const changeTagOffsetPlayer = (value:string, index:number) => {
    const newEntity = JSON.parse(JSON.stringify(entity));
    if (value) {
      newEntity.tags[index].offset = value.split(',').map(offset => {
        return Number(offset);
      });
    } else {
      newEntity.tags[index].offset = null;
    }
    setEntity(newEntity);
  };
  //TODO: make a reusable function for these repeated blocks
  const changeMiddleName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.middleName = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeFootballName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.footballName = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeDisplayName = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.displayName = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeWeek = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.week = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeHomeTown = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.homeTown = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeCollegeId = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.collegeId = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeGsisId = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.gsisId = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeSeason = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.season = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeSeasonType = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.seasonType = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeEsbId = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.esbId = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeCurrentTeamId = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.currentTeamId = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeTeamSeq = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.teamSeq = event.target.value;
      setExtraData(newExtraData);
    }
  };
  const changeStats = (event:React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== ' ') {
      const newExtraData = JSON.parse(JSON.stringify(extraData));
      newExtraData.stats = event.target.value;
      setExtraData(newExtraData);
    }
  };

  const handleAddTag = () => {
    const newEntity = JSON.parse(JSON.stringify(entity));
    newEntity.tags.push(getInitialTag());
    setEntity(newEntity);
  };

  const getInitialTag = () => {
    return {
      tagGroupId: null,
      group: '',
      name: '',
      offset: null,
      active: false,
      id: ''
    };
  };

  const getInitialExtraData = (type?:string) => {
    const entityType = type || (entity && entity.type);
    return {
      status: entityType === ENTITY_VALUES.BALL ? ENTITY_STATUS_TYPES.ACT : '',
      firstName: '',
      weight: '',
      lastName: '',
      teamId,
      birthDate: '',
      height: '',
      nflId: '',
      position: '',
      jerseyNumber: null,
      fitnessLevel: '',
      collegeName: '',
      positionGroup: '',
      middleName: '',
      footballName: '',
      displayName: '',
      week: '',
      homeTown: '',
      collegeId: '',
      gsisId: '',
      season: '',
      seasonType: '',
      esbId: '',
      currentTeamId: '',
      teamSeq: '',
      stats: '',
    };
  };

  const onEntering = () => {
    setActiveTab('tags');
    setEntity(selectedEntity ? JSON.parse(JSON.stringify(selectedEntity)) : { ...getInitialEntity(), group: ENTITY_GROUPS(teamId)[0] });
    setExtraData(!selectedEntity ? getInitialExtraData() : !selectedEntity.extra_data ?
      getInitialExtraData() : JSON.parse(JSON.stringify(selectedEntity.extra_data))); 
  };

  const shouldDisableSaveBtn = () => {
    if (isDataOpsPage) {
      return !entity.namespaced_id || !entity.id;
    } else {
      const entitiesPageVal = entitiesPageValidator(entity, extraData, isNcaaLeague);
      return entitiesPageVal;
    }
  };

  const modalTitle = addMode ? 'Add Entity' : 'Edit Entity';
  const typeDropdownItems = ENTITY_TYPES.map((type, index) =>
    <Dropdown.Item
      id={`entity_type_${type.value}`}
      key={index} onClick={() => {
      setEntity({ ...getInitialEntity(), type: type.value });
      setExtraData(getInitialExtraData(type.value));
    }}
    >
      {type.label}
    </Dropdown.Item>);
  const modelDropdownItems = ENTITY_MODELS(entity && entity.type ? entity.type : ENTITY_VALUES.PLAYER).map((model, index) =>
    <Dropdown.Item key={index} id={`entity_model_${model.value}`}  onClick={() => changeEntityModel(model.value)} >
      {model.label}
    </Dropdown.Item>);
  const groupDropdownItems = ENTITY_GROUPS(teamId).map((group, index) =>
    <Dropdown.Item key={index} id={`entity_group_${group}`} onClick={() => changeEntityGroup(group)}>
      {group}
    </Dropdown.Item>);

  const entityType = !entity ? ENTITY_VALUES.PLAYER : !entity.type ? ENTITY_VALUES.PLAYER : entity.type;
  const entityModel = !entity ? ENTITY_MODELS(entityType)[0].value : !entity.model ? ENTITY_MODELS(entityType)[0].value : entity.model;
  const entityGroup = !entity ? ENTITY_GROUPS(teamId)[0] : !entity.group ? ENTITY_GROUPS(teamId)[0] : entity.group;

  return <>
    <Modal
      className="modal-content-03"
      show={show === 0}
      onHide={onHide}
      onEntering={onEntering}
      onExited={onExited}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <div className="m-profil-h">{modalTitle}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="m-entities-t">
          <div className="d-entities-t">
            <p>Entity Type</p>
            <Dropdown>
              <Dropdown.Toggle id="dropdown-basic-entity_type" className="btn-gray-l" disabled={!addMode}>
                {entityType}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {typeDropdownItems}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div className="d-entities-t">
            <p>Entity Model</p>
            <Dropdown>
              <Dropdown.Toggle id="dropdown-basic-entity_model" className="btn-gray-l" disabled={!addMode}>
                {entityModel}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {modelDropdownItems}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <p>Group</p>
            <Dropdown>
              <Dropdown.Toggle id="dropdown-basic-entity_group" className="btn-gray-l">
                {entityGroup}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {groupDropdownItems}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <div className="m-entities-t">
          <div className="d-entities-t">
            <div className="material-textbox" id="first-name-input">
              <input
                id='entity_id'
                className="material-input" required
                value={entity && entity.id ? entity.id : ''}
                onChange={(event) => changeEntityId(event)}
              />
              <span className="material-label">Entity ID</span>
            </div>
            {!isDataOpsPage && <label style={{width: '230px'}}>Note: If you do not have a valid Entity ID, leave this field blank and one will be created
              automatically.</label>}
          </div>
          <div className="d-entities-t">
            {entity.type === ENTITY_VALUES.PLAYER && !isDataOpsPage ? //when it is player, the field autofills and is not editable
              <>
                <div className="material-textbox" id="first-name-input" title="please edit this field from extra data">
                  {!addMode && <p style={{fontSize: '12px', color:'#007ACA', position:'absolute', top:'-10px'}}>Entity Name</p>}
                  <input
                    id='entity_name'
                    className="material-input no-asterisk"
                    value={entity && entity.name ? entity.name : ''}
                    disabled
                  />
                  {addMode && <span className="material-label">Entity Name</span>}
                </div>
              </>
              : // cuando es otra cosa que player o estamos en dataops (la mayoria de los casos son ball) si se puede editar este campo
              <>
                <div className="material-textbox" id="first-name-input">
                  <input
                    id='entity_name'
                    className="material-input" required
                    value={entity && entity.name ? entity.name : ''}
                    onChange={event => changeEntityName(event)}
                  />
                  <span className="material-label">Entity Name</span>
                </div>
              </>
            }
            {/*<div className="material-textbox" id="first-name-input" title="please edit this field from extra data">
             {entity.type !== ENTITY_VALUES.BALL ? <span className="material-label">Entity Name</span> : <p>Entity Name</p>}
              <input className="material-input" required={false}
                     value={entity ? entity.name : null}
                     onChange={(event) => changeEntityName(event)}
                     disabled={entity.type !== ENTITY_VALUES.BALL}
              />
            </div>*/}
          </div>
          {(isDataOpsPage || entity.type === ENTITY_VALUES.BALL) && <div>
            <div className="material-textbox" id="first-name-input">
              <input
                id='entity_namespace_id'
                className="material-input" required
                value={entity && entity.namespaced_id ? entity.namespaced_id : ''}
                onChange={(event) => changeEntityNamespaceId(event)}
              />
              <span className="material-label">Namespace ID</span>
            </div>
          </div>}
        </div>
        <div className="t-entities-t">
          <Tabs activeKey={activeTab} id="uncontrolled-tab-example" onSelect={eventKey => eventKey && setActiveTab(eventKey)}>
            <Tab eventKey="tags" title="Tags">
              <div>
                <TagsTable
                  entity={entity}
                  setEntity={setEntity}
                  changeTagGroupPlayer={changeTagGroupPlayer}
                  changeTagOffsetPlayer={changeTagOffsetPlayer}
                  changeTagNamePlayer={changeTagNamePlayer}
                />
                <button className="btn-tag" onClick={() => handleAddTag()}>Add Tags</button>
              </div>
            </Tab>
            {entity.type !== ENTITY_VALUES.BALL && entity.type !== ENTITY_VALUES.OBJECT &&  <Tab eventKey="extra" title="Extra Data">
              {isDataOpsPage && !isToday ?
                <DataOpsExtraData
                  addEntity={null}
                  changeBirthday={changeBirthday}
                  changeCollegeId={changeCollegeId}
                  changeCollegeName={changeCollegeName}
                  changeCurrentTeamId={changeCurrentTeamId}
                  changeDisplayName={changeDisplayName}
                  changeEsbId={changeEsbId}
                  changeFirstName={changeFirstName}
                  changeFootballName={changeFootballName}
                  changeGsisId={changeGsisId}
                  changeHeight={changeHeight}
                  changeHomeTown={changeHomeTown}
                  changeJerseyNumber={changeJerseyNumber}
                  changeLastName={changeLastName}
                  changeMiddleName={changeMiddleName}
                  changeNflId={changeNflId}
                  changeSeason={changeSeason}
                  changeSeasonType={changeSeasonType}
                  changeTeamSeq={changeTeamSeq}
                  changeWeek={changeWeek}
                  changeWeight={changeWeight}
                  entity={entity}
                  extraData={extraData}
                  setExtraData={setExtraData}
                  teamId={teamId}
                  changeStats={changeStats}
                /> :
                //TODO: fix optional props
                //@ts-ignore
                <ExtraDataForm
                  addEntity={addMode}
                  changeBirthday={changeBirthday}
                  changeCollegeName={changeCollegeName}
                  changeFirstName={changeFirstName}
                  changeHeight={changeHeight}
                  changeJerseyNumber={changeJerseyNumber}
                  changeLastName={changeLastName}
                  changeNflId={changeNflId}
                  changeWeight={changeWeight}
                  entity={entity}
                  extraData={extraData}
                  setExtraData={setExtraData}
                  teamId={teamId}
                  changeStats={changeStats}
                />
              }
            </Tab>}
          </Tabs>
          <div className="f-entities">
            <button className="btn-transparet" onClick={() =>{
               setExtraData(getInitialExtraData());
                onHide();
                }}>Cancel</button>
            <button className="btn-green"
                    disabled={shouldDisableSaveBtn()}
                    style={(entity.tags.some((tag:Tag) => (!(tag.id && tag.name && tag.group && tag.offset))) || shouldDisableSaveBtn()) ? {
                      opacity: '0.5',
                      pointerEvents: 'none'
                    } : {}}
                    onClick={() => handleSaveClick()}>
              Save
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  </>;
};
 export default EntityModal;
